/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import DOMPurify from "dompurify";
import GeoJSON from "ol/format/GeoJSON";
import { GeometryCollection } from "ol/geom";
import { memo, useEffect, useState } from "react";
import { MdDateRange, MdZoomInMap } from "react-icons/md";

import { LS_SITUATION_COLLAPSED_KEY } from "../constants";
import useAppSelector from "../utils/hooks/useAppSelector";

import Button from "./controls/Button";
import {
  IcDbNavigationExpandLess24,
  IcDbNavigationExpandMore24,
} from "./icons";
import LineIconSvg from "./LineIconSvg";

import type { Situation as SituationType } from "../model/reducers";

function toShortDate(
  date: Date,
  showTime: boolean | string,
  showShowTime = false,
) {
  const time = date.toLocaleTimeString(["de"], {
    hour: "2-digit",
    minute: "2-digit",
  });
  const dateString = date.toLocaleDateString(["de"], {
    day: "2-digit",
    month: "short",
    weekday: "short",
  });

  return `${dateString}${showTime && (showTime !== time || showShowTime) ? ` ${time}` : ``}`
    .replace(",", "")
    .replace(/\./, "")
    .replace(/\.$/, "");
}

const geojson = new GeoJSON();
// const padding = new URLSearchParams(window.location.search).get("padding") || 0;

function Situation({ situation }: { situation: SituationType }) {
  const map = useAppSelector((state) => state.map);
  const config = useAppSelector((state) => state.config);
  const selectedNotificationIds = useAppSelector(
    (state) => state.selectedNotificationIds,
  );
  const localStorage = useAppSelector((state) => state.localStorage);
  const collapsedStorageKey = `${LS_SITUATION_COLLAPSED_KEY}.${situation.id}`;
  const [isOpen, setIsOpen] = useState<boolean>(() => {
    const storedCollapsed = localStorage.getItem(collapsedStorageKey);
    if (storedCollapsed !== null) {
      return storedCollapsed !== "true";
    }
    return selectedNotificationIds?.includes(situation.id) || false;
  });

  const toggleOpen = (open: boolean) => {
    setIsOpen(open);
    localStorage.setItem(collapsedStorageKey, String(!open));
  };

  const hasSelectedSituation = !!selectedNotificationIds?.length;
  const isSelected = selectedNotificationIds?.includes(situation.id);

  useEffect(() => {
    if (hasSelectedSituation) {
      setIsOpen(!!isSelected);
    }
  }, [hasSelectedSituation, isSelected]);

  // const dateStringOptions: Intl.DateTimeFormatOptions = {
  //   day: "numeric",
  //   hour: "2-digit",
  //   minute: "2-digit",
  //   month: "long",
  //   //timeStyle: "short",
  //   // weekday: "long",
  //   year: "numeric",
  // };

  // TODO:  What do w edo with tiem start and end
  const start = new Date(situation.affectedTimeIntervalsStart as string);
  const end = new Date(situation.affectedTimeIntervalsEnd as string);

  let opacity = 1;
  if (hasSelectedSituation) {
    opacity = isOpen ? 1 : 0.5;
  }

  return (
    <>
      {situation?.publications?.map((publication) => {
        const { description, summary } =
          publication.textualContentLarge?.de || {};
        const hasInfo = !!description;
        let lines =
          publication?.publicationLineNames?.map((publicationLine) =>
            publicationLine.replace(" ", ""),
          ) ?? [];
        lines = lines.filter((line) => line.includes("S"));

        const allGeoms = [
          ...(publication.publicationLines?.flatMap(
            (pl) =>
              pl.lines?.flatMap(
                (line) =>
                  line.geometry?.map((g) => g.geom).filter(Boolean) ?? [],
              ) ?? [],
          ) ?? []),
          ...(publication.publicationStops?.flatMap(
            (stop) => stop.geometry?.map((g) => g.geom).filter(Boolean) ?? [],
          ) ?? []),
        ];

        return (
          <div
            key={`${situation.id} ${publication.id}`}
            onClick={() => {
              toggleOpen(!isOpen);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                toggleOpen(!isOpen);
              }
            }}
            role="button"
            style={{
              backgroundColor: "white",
              boxShadow:
                "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
              display: "flex",
              flexDirection: "row",
              maxHeight: isOpen ? 10000 : 80,
              opacity: opacity,
              overflow: "hidden",
              paddingBottom: 10,
              paddingRight: 10,
              paddingTop: 10,
              position: "relative",
              transition: "max-height 0.3s ease, opacity 0.3s ease",
            }}
            tabIndex={0}
          >
            {hasInfo && !isOpen && (
              <div
                style={{
                  backgroundImage:
                    "linear-gradient(rgba(0, 0, 0, 0), rgb(255, 255, 255))",
                  bottom: 0,
                  display: "block",
                  height: 40,
                  position: "absolute",
                  width: "100%",
                }}
              ></div>
            )}
            <div
              style={{
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                gap: 2,
                justifyContent: "flex-start",
                maxWidth: 64,
                minWidth: 64,
                width: 64,
              }}
            >
              {/* <NotificationIcon
                category={situation.reasons[0].name}
                style={{ width: 36 }}
              /> */}
              {
                //!isAllLinesDisplayed &&
                lines.map((line) => {
                  return (
                    <LineIconSvg
                      key={line}
                      line={{ name: line.replace(" ", "") }}
                    />
                  );
                })
              }
              {/* if no lines display the sbahn icon */}
              {!lines.length && (
                // || isAllLinesDisplayed
                <LineIconSvg line={null} />
              )}
            </div>
            <div style={{ flexGrow: 1, minWidth: 0 }}>
              <div
                style={{
                  alignItems: "center",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <h4
                  // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(summary ?? ""),
                  }}
                  style={{
                    flex: "1 1 0",
                    fontWeight: "bold",
                    marginBottom: 5,
                    marginTop: 0,
                    overflowWrap: "anywhere",
                  }}
                ></h4>
                <div
                  style={{
                    display: "flex",
                    flexShrink: 0,
                    gap: 10,
                    height: 24,
                  }}
                >
                  {config.notificationControls && allGeoms.length > 0 && (
                    <Button
                      disabled={hasSelectedSituation && !isOpen}
                      onClick={(evt: React.MouseEvent<HTMLButtonElement>) => {
                        const collection = new GeometryCollection(
                          allGeoms.map((geom) => geojson.readGeometry(geom)),
                        );
                        const padding = 50;
                        map.getView().fit(collection.getExtent(), {
                          duration: 500,
                          padding: [padding, padding, padding, padding],
                        });
                        evt.stopPropagation();
                      }}
                      title="Auf Störungsmeldungen zoomen"
                    >
                      <MdZoomInMap focusable={false} size={24} />
                    </Button>
                  )}
                  {config.notificationControls && hasInfo && !isOpen && (
                    //onClose &&
                    <Button
                      disabled={false}
                      onClick={() => {
                        toggleOpen(true);
                      }}
                      title="Weniger Informationen"
                    >
                      <IcDbNavigationExpandMore24 focusable={false} size={24} />
                    </Button>
                  )}
                  {config.notificationControls && hasInfo && isOpen && (
                    //onClose &&
                    <Button
                      disabled={false}
                      onClick={() => {
                        toggleOpen(false);
                      }}
                      title="Weitere Informationen"
                    >
                      <IcDbNavigationExpandLess24 focusable={false} size={24} />
                    </Button>
                  )}
                </div>
              </div>

              <p
                style={{
                  alignItems: "center",
                  display: "flex",
                  gap: 10,
                  margin: 0,
                }}
              >
                <MdDateRange />
                {
                  // timeStart && timeEnd
                  //       ? `${toShortDate(start)} – ${toShortDate(end)} ${timeStart.substring(0, 5)}-${timeEnd.substring(0, 5)}` // prettier-ignore
                  // :
                  `${toShortDate(start, '00:00', true)} – ${toShortDate(end, '23:59', true)}` // prettier-ignore
                }
              </p>
              {description && (
                <div
                  // eslint-disable-next-line @eslint-react/dom-no-dangerously-set-innerhtml
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(description),
                  }}
                  style={{
                    minWidth: 0,
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                />
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

export default memo(Situation);
